import { useCallback } from "react"
import { useAuthApi } from "../../infra/auth"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { setAuthenticationState, setLoginFormErrorsState, setLoginFormLoginClienteState, setLoginFormLoginUsuarioState, setLoginFormSenhaUsuarioState, setLoginFormTouchedState, setSessionConfig, setTokenState, validateLoginForm } from "../../application/states/auth"
import { useLocalStorageService } from "../local-storage"
import { decodeJwt } from 'jose'
import { useUserApi } from "../../infra/user"
import { setCurrentUserState } from "../../application/states/user"
import { useUIService } from "../ui"

export const useAuthService = () => {
  // Dispatch
  const dispatch = useAppDispatch()
  // State
  const { loginForm } = useAppSelector((state) => state.auth)
  // API
  const { loginApi, insertAcessoApi, generateLegacyRedirectApi, refreshApi, getSessionTimeoutApi } = useAuthApi()
  const { getUserByIdApi } = useUserApi()
  // Use Case / Service
  const { setItem, getItem, removeItem } = useLocalStorageService()
  const { setAlert, setLoading } = useUIService()

  // Métodos
  const logout = useCallback((options?: { isIpBlock?: boolean; message?: string; redirectToModules?: boolean }) => {
    dispatch(setTokenState(''))
    removeItem({ key: 'ACCESS_TOKEN' })
    removeItem({ key: 'LOGIN_TIMESTAMP' })
    window.localStorage.clear()
    window.sessionStorage.clear()

    let loginUrl = String(process.env.REACT_APP_VELES_APP_URL_LOGIN)
    if (!loginUrl.includes('/authentication/login')) {
      loginUrl = loginUrl.replace('/auth/login', '/authentication/login')
    }

    if (options?.message) {
      window.location.href = `${loginUrl}?message=${encodeURIComponent(options.message)}`
    } else if (options?.redirectToModules) {
      const appBaseUrl = loginUrl.replace('/authentication/login', '').replace('/auth/login', '')
      window.location.href = `${appBaseUrl}/modulos`
    } else {
      window.location.href = `${loginUrl}?logout=true`
    }
  }, [dispatch, removeItem])

  const refresh = useCallback(async () => {
    try {
      const tokenStorage = getItem({
        key: 'ACCESS_TOKEN'
      })
      if (!tokenStorage || tokenStorage === 'not_valid' || tokenStorage === '') return

      const response = await refreshApi(tokenStorage)
      const autenticado = response.Autenticado ?? (response as any).autenticado
      const novoToken = response.Token ?? (response as any).token
      const mensagem = response.Mensagem ?? (response as any).mensagem

      if (novoToken && novoToken !== '') {
        dispatch(setTokenState(novoToken))
        setItem({
          key: 'ACCESS_TOKEN',
          value: novoToken
        })
      } else if (autenticado === false) {
        console.warn("[DASH-REFRESH] Sessão invalidada pelo servidor:", mensagem)
        const isIpBlock = Boolean(mensagem && typeof mensagem === 'string' && mensagem.toLowerCase().includes("ip"))
        if (!isIpBlock) {
          logout({ redirectToModules: true })
        }
      }
    } catch (error: any) {
      console.error("Erro no refresh background (veles-dash):", error);
      const resData = error?.response?.data || {};
      const msg = resData.message || resData.error || resData.Mensagem || resData.mensagem || "";
      const isIpBlock = Boolean(msg && typeof msg === 'string' && msg.toLowerCase().includes("ip"));
      if (!isIpBlock && error?.response?.status === 401) {
        logout({ redirectToModules: true });
      }
    }
  }, [getItem, refreshApi, dispatch, setItem, logout])

  const decodeToken = useCallback((data: {
    token: string
  }) => {
    const decoded = decodeJwt(data.token)

    return decoded
  }, [])

  const fetchSessionConfig = useCallback(async () => {
    try {
      const response = await getSessionTimeoutApi()
      const { sessionTimeoutSeconds, idVelesCliente } = response

      dispatch(setSessionConfig({
        timeout: sessionTimeoutSeconds,
        idVelesCliente: idVelesCliente
      }))

      return sessionTimeoutSeconds
    } catch (error) {
      return Number(process.env.SESSION_TIMEOUT_SECONDS)
    }
  }, [getSessionTimeoutApi, dispatch])

  const setupSession = useCallback(async () => {
    try {
      const tokenStorage = getItem({
        key: 'ACCESS_TOKEN'
      })
      const timestampStorage = getItem({
        key: 'LOGIN_TIMESTAMP'
      })

      if (tokenStorage) {
        dispatch(setTokenState(tokenStorage))

        if (timestampStorage) {
          dispatch(setSessionConfig({
            timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
            timestamp: Number(timestampStorage)
          }))
        } else {
          const now = Date.now()
          setItem({ key: 'LOGIN_TIMESTAMP', value: String(now) })
          dispatch(setSessionConfig({
            timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
            timestamp: now
          }))
        }

        await fetchSessionConfig()

        const decoded = decodeToken({
          token: tokenStorage
        })

        const userId = decoded.IdUsuario ? (decoded.IdUsuario as number) : undefined

        if (userId) {
          const user = await getUserByIdApi({
            idUsuario: userId
          })

          dispatch(setCurrentUserState(user.usuario))
        }
      } else {
        dispatch(setTokenState('not_valid'))
      }
    } catch (error: any) {
      const resData = error?.response?.data || {};
      const msg = resData.message || resData.error || resData.Mensagem || resData.mensagem || "";
      const isIpBlock = Boolean(msg && typeof msg === 'string' && msg.toLowerCase().includes("ip"));
      if (isIpBlock) {
        logout({ isIpBlock: true, message: msg });
        return;
      }
      if (error.response?.status === 401) {
        // Limpar token inválido
        const tokenStorage = getItem({
          key: 'ACCESS_TOKEN'
        })
        if (tokenStorage && tokenStorage !== '') {
          dispatch(setTokenState('not_valid'))
          removeItem({
            key: 'ACCESS_TOKEN'
          })
          setAlert({
            message: 'Sessão expirada. Faça login novamente.',
            type: 'error'
          })
        }
      } else if (error.response?.status === 500) {
        // Erro do servidor: não remover token (pode ser temporário); não dar throw para não quebrar a sessão
        setAlert({
          message: error.response?.data?.message || error.response?.data?.error || 'Erro temporário ao carregar usuário. Tente novamente.',
          type: 'warning'
        })
      } else {
        // Outros erros (rede, 404, etc.): não limpar token para respeitar expiração; apenas avisar
        setAlert({
          message: error.response?.data?.error || error.response?.data?.message || 'Erro ao carregar sessão. Tente recarregar a página.',
          type: 'warning'
        })
      }
    }
  }, [decodeToken, dispatch, getItem, getUserByIdApi, removeItem, setAlert])

  //Login Form Actions
  const setLoginFormLoginCliente = useCallback((loginCliente: string) => {
    dispatch(setLoginFormLoginClienteState({ loginCliente }))
  }, [dispatch])

  const setLoginFormLoginUsuario = useCallback((loginUsuario: string) => {
    dispatch(setLoginFormLoginUsuarioState({ loginUsuario }))
  }, [dispatch])

  const setLoginFormSenhaUsuario = useCallback((senhaUsuario: string) => {
    dispatch(setLoginFormSenhaUsuarioState({ senhaUsuario }))
  }, [dispatch])

  const login = useCallback(async (data: { idProduto: number }) => {
    const loadingId = Math.random().toString()
    try {
      setLoading({
        loading: true,
        message: "Aguarde...",
        id: loadingId
      })

      const errors = validateLoginForm(loginForm.fields)
      if (errors.length > 0) {
        dispatch(setLoginFormErrorsState({
          errors
        }))
        dispatch(setLoginFormTouchedState({
          touched: true
        }))
      } else {

        dispatch(setLoginFormErrorsState({
          errors: []
        }))
        dispatch(setLoginFormTouchedState({
          touched: false
        }))

        const response = await loginApi({
          loginCliente: loginForm.fields.loginCliente,
          loginUsuario: loginForm.fields.loginUsuario,
          senhaUsuario: loginForm.fields.senhaUsuario,
          idProduto: data.idProduto
        })

        const timestamp = Date.now()
        setItem({
          key: 'ACCESS_TOKEN',
          value: response.token
        })
        setItem({
          key: 'LOGIN_TIMESTAMP',
          value: String(timestamp)
        })

        dispatch(setTokenState(response.token))
        dispatch(setAuthenticationState(response.authentication))
        dispatch(setCurrentUserState(response.usuario))
        dispatch(setSessionConfig({
          timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
          timestamp: timestamp
        }))

        await fetchSessionConfig()
      }

    } catch (error: any) {
      // Em caso de erro (incluindo 401), limpar estado de autenticação
      if (error.response?.status === 401) {
        dispatch(setTokenState('not_valid'))
        removeItem({
          key: 'ACCESS_TOKEN'
        })
      }
      
      setAlert({
        message: error.response?.data?.error || error.response?.data?.message || 'Erro desconhecido',
        type: 'error'
      })
    } finally {
      setLoading({
        loading: false,
        id: loadingId
      })
    }
  }, [dispatch, loginApi, loginForm.fields, setAlert, setItem, setLoading])

  const insertAcesso = useCallback(async (idProduto: number): Promise<{ redirectUrl: string, method: 'GET' | 'POST' | 'GET_NOREFERRER' } | null> => {
    try {
      const tokenStorage = getItem({
        key: 'ACCESS_TOKEN'
      })

      if (tokenStorage) {
        dispatch(setTokenState(tokenStorage))

        const decoded = decodeToken({
          token: tokenStorage
        })

        const idUsuario = decoded.IdUsuario ? (decoded.IdUsuario as number) : undefined

        if (idUsuario) {
          const insertAcesso = await insertAcessoApi({
            IdProduto: idProduto,
            IdUsuario: idUsuario
          })

          dispatch(setTokenState(insertAcesso.token))
          dispatch(setCurrentUserState(insertAcesso.usuario))

          removeItem({
            key: 'ACCESS_TOKEN'
          })

          setItem({
            key: 'ACCESS_TOKEN',
            value: insertAcesso.token
          })

          // Verificar se é produto legado e gerar URL de redirecionamento
          const legacyProducts = [1, 3, 6, 9] // id_veles, ia_veles, geo_veles, auto_veles
          if (legacyProducts.includes(idProduto)) {
            const redirectResult = await generateLegacyRedirectApi({
              IdAcesso: insertAcesso.acesso.IdAcesso,
              IdProduto: idProduto
            })
            return redirectResult
          }

          return null
        }
      }
      return null
    } catch (error: any) {
      setAlert({
        message: error.response?.data?.error || 'Erro desconhecido',
        type: 'error'
      })
      throw error
    }
  }, [decodeToken, dispatch, generateLegacyRedirectApi, getItem, insertAcessoApi, removeItem, setAlert, setItem])

  const setToken = useCallback(() => {
    const token = getItem({
      key: 'ACCESS_TOKEN'
    })

    if (token) {
      dispatch(setTokenState(token))
    }
  }, [dispatch, getItem])

  return {
    // Login Form Actions
    setLoginFormLoginCliente,
    setLoginFormSenhaUsuario,
    setLoginFormLoginUsuario,
    login,
    insertAcesso,
    setupSession,
    logout,
    setToken,
    refresh,
    fetchSessionConfig
  }
}